import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { BILLING_VERSION } from '../store/company';
import {
    loadUserData, loadHeavyJobCards, loadCrewShifts, loadBillingSignatures, loadAttendanceRoster, pNum,
} from '../db/company';
import { normInvStatus } from './billing';
import { parseSheets, ENV_STATUS } from './billingTimecardApproval';
import { parseDocs, coverageMap, envelopesByDoc, cellDocStatus } from './billingTicketDocs';
import { rosterShiftOf } from './billingDailyLabor';
import strikeLogo from '../assets/logo.png';
import voltaLogo from '../assets/voltagrid.png';

// Trailer TV board for the VoltaGrid jobs (54026070 / 54026071).
//
// A hands-off, full-screen board for the monitor in the field office, served
// on its own route (/billing-tv, a standalone Main with no menus) so the
// trailer PC can bookmark it and come straight back after a reboot. It
// answers three questions from across the room:
//
//   1. How big was the crew, day and night, and how is that trending?
//   2. Where does each work day's field ticket stand with the client — not
//      sent, waiting on a signature, signed, returned, billed?
//   3. What is missing right now? (a list built from the data, not a fixed
//      set of tiles — it reads "All caught up" when nothing is)
//
// The client walks through that trailer, so the board shows no dollars, cost,
// markup or rates — only headcount, ticket status and the billed-through date.
//
// Ticket status is resolved exactly like the Time Card Approval tracker's day
// chips: a ticket document covering the cell (its envelope, else its stage),
// else the newest envelope for the date+shift, else the tracked sheet's stage.
// A person's shift for a day is resolved like the tracker too: the day's
// manual correction ('tcshift'), then the client's attendance roster, then
// the HeavyJob card shift.
//
// Feeds, per job, refreshed every five minutes:
//   - readUserData at BILLING_VERSION, tab 'Billing' — tracked sheets, ticket
//     documents, shift corrections, tracking start, summaries;
//   - readBillingSignatures — DocuSign envelopes and their event history;
//   - readAttendanceRoster — the client's Day/Night roster;
//   - readCrewShifts — person-per-day HeavyJob crew for the chart. A job it
//     has not answered for yet falls back to the live HeavyJob cards' day
//     totals (readHeavyJobCards, no day/night split); one it has answered for
//     keeps its last crew through a failed pass.

export const DASHBOARD_JOBS = ['54026070', '54026071'];
export const SHIFTS = ['Day', 'Night'];

const WINDOW_DAYS = 21;
const REFRESH_MS = 5 * 60 * 1000;
// A ticket out for signature longer than this is worth chasing.
const WAIT_ALERT_DAYS = 2;

// ── Pure helpers (exported for the tests) ──

const ymd = (m) => m.format('YYYY-MM-DD');
const dayDiff = (a, b) => moment.utc(b, 'YYYY-MM-DD').diff(moment.utc(a, 'YYYY-MM-DD'), 'days');

/** The window of calendar days ending yesterday (oldest first). */
export const windowDays = (today = ymd(moment()), n = WINDOW_DAYS) => {
    const out = [];
    for (let i = n; i >= 1; i -= 1) out.push(moment.utc(today, 'YYYY-MM-DD').subtract(i, 'day').format('YYYY-MM-DD'));
    return out;
};

export const isWeekend = (d) => [0, 6].includes(moment.utc(d, 'YYYY-MM-DD').day());

// Roster helpers live with the ticket builder so the tracker and this board
// resolve a person's shift and billed classification the same way.
export { rosterShiftOf };

/** date -> { staffId: 'Day' | 'Night' } manual corrections. */
export const shiftOverridesOf = (rows) => {
    const m = {};
    (rows || []).forEach((x) => {
        if (x?.Forecast_Field_Name !== 'tcshift') return;
        try {
            const p = x.Notes ? JSON.parse(x.Notes) : null;
            if (p && p.d && p.o) m[p.d] = p.o;
        } catch (e) { /* malformed blob — ignore */ }
    });
    return m;
};

/**
 * Crew per day per shift from person-day rows. Returns date -> { Day, Night,
 * total, unassigned, split } where unassigned counts people the roster doesn't
 * place (their shift fell through to the HeavyJob card).
 */
export const crewByDay = (people, rosterShift, overrides) => {
    const out = {};
    const seen = {};
    (people || []).forEach((r) => {
        const d = r.Work_Date;
        const id = String(r.Staff_ID || '');
        if (!d || !id) return;
        const k = `${d}|${id}`;
        if (seen[k]) return;
        seen[k] = true;
        const ov = overrides?.[d]?.[id];
        const ros = rosterShift?.[id];
        const sh = ov || ros || (Number(r.Shift) === 2 ? 'Night' : 'Day');
        const e = out[d] || (out[d] = { Day: 0, Night: 0, total: 0, unassigned: 0, split: true });
        e[sh] += 1;
        e.total += 1;
        if (!ov && !ros) e.unassigned += 1;
    });
    return out;
};

/** Fallback crew totals (no split) from a day list: [{ Work_Date, Staff }]. */
export const crewFromDates = (dates) => {
    const out = {};
    (dates || []).forEach((r) => {
        const n = Math.round(pNum(r.Staff));
        if (r.Work_Date && n) out[r.Work_Date] = { Day: 0, Night: 0, total: n, unassigned: 0, split: false };
    });
    return out;
};

/** Newest date+shift envelope per `${date}_${shift}` (the read path is newest-first). */
export const sheetEnvelopes = (envelopes) => {
    const m = {};
    (envelopes || []).forEach((e) => {
        if (e.Shift === 'Force' || e.Shift === 'Doc') return;
        const k = `${e.Work_Date}_${e.Shift}`;
        if (!m[k]) m[k] = e;
    });
    return m;
};

const DEAD_ENV = ['voided', 'expired', 'failed'];

/**
 * One cell's ticket state: 'signed' | 'returned' | 'waiting' | 'prep' | 'none',
 * plus the live envelope (for opened / sent-at / reason) when there is one.
 */
export const cellState = (d, sh, ctx) => {
    const cov = cellDocStatus(d, sh, ctx.coverage, ctx.sigByDoc);
    let stage = null;
    let env = null;
    if (cov) {
        stage = cov.stage;
        env = cov.env && !DEAD_ENV.includes(String(cov.env.Status).toLowerCase()) ? cov.env : null;
    } else {
        const e = ctx.sigBy[`${d}_${sh}`];
        if (e && !DEAD_ENV.includes(String(e.Status).toLowerCase())) {
            env = e;
            stage = ENV_STATUS[String(e.Status).toLowerCase()]?.stage || null;
        }
        if (!stage) stage = ctx.sheets[`${d}_${sh}`] || null;
    }
    const state = stage === 'Signed' ? 'signed'
        : stage === 'Declined' ? 'returned'
            : stage === 'Sent for Signature' ? 'waiting'
                : stage ? 'prep' : 'none';
    return { state, env, doc: cov?.doc || null };
};

/** Work dates on submitted (uploaded / invoiced) summaries, plus kicked-back summaries. */
export const billedDaysOf = (rows) => {
    const status = {};
    (rows || []).forEach((x) => {
        if (x?.Forecast_Field_Name !== 'invoice') return;
        try {
            const p = x.Notes ? JSON.parse(x.Notes) : null;
            if (p && p.no) status[p.no] = normInvStatus(p.status);
        } catch (e) { /* malformed blob — ignore */ }
    });
    const days = new Set();
    let through = '';
    (rows || []).forEach((x) => {
        if (x?.Forecast_Field_Name !== 'line' || !x.Forecast_Value) return;
        if (!['Uploaded to FTMS', 'Invoiced'].includes(status[x.Forecast_Value])) return;
        try {
            const s = x.Notes ? JSON.parse(x.Notes) : null;
            if (s?.d) { days.add(s.d); if (s.d > through) through = s.d; }
        } catch (e) { /* malformed blob — ignore */ }
    });
    const kickedBack = Object.entries(status).filter(([, s]) => s === 'Kicked Back').map(([no]) => no).sort();
    return { days, through, kickedBack };
};

/** Everything the board shows for one job, from its raw feeds. */
export const jobModel = (job, feed, days) => {
    const rows = feed.rows || [];
    const envelopes = feed.sigs || [];
    const rosterShift = rosterShiftOf(feed.roster, job);
    const overrides = shiftOverridesOf(rows);
    const crew = feed.people ? crewByDay(feed.people, rosterShift, overrides) : crewFromDates(feed.dates);
    const sheets = {};
    parseSheets(rows).forEach((s) => { sheets[`${s.d}_${s.sh}`] = s.st; });
    const ctx = {
        coverage: coverageMap(parseDocs(rows)),
        sigByDoc: envelopesByDoc(envelopes),
        sigBy: sheetEnvelopes(envelopes),
        sheets,
    };
    const startRow = rows.find((x) => x?.Forecast_Field_Name === 'tcstart');
    const trackStart = /^\d{4}-\d{2}-\d{2}$/.test(String(startRow?.Forecast_Value || '')) ? startRow.Forecast_Value : '';
    const billed = billedDaysOf(rows);
    const hasRoster = Object.keys(rosterShift).length > 0;

    // A shift "worked" a day when crew was split onto it. Without a split
    // the day crew is assumed, and a night cell only counts once tracked.
    const cells = [];
    days.forEach((d) => {
        const c = crew[d];
        SHIFTS.forEach((sh) => {
            const st = cellState(d, sh, ctx);
            const worked = c
                ? (c.split ? c[sh] > 0 : (sh === 'Day' || st.state !== 'none'))
                : st.state !== 'none';
            cells.push({ job, d, sh, worked, billed: billed.days.has(d), ...st });
        });
    });
    return { job, crew, cells, trackStart, billed, hasRoster, envelopes };
};

const fmtDay = (d) => moment.utc(d, 'YYYY-MM-DD').format('ddd M/D');
// Whole days since a UTC timestamp, as the attention list and the KPI both say it.
export const sentAgo = (t, now = moment()) => {
    if (!t) return '';
    const n = Math.max(0, now.diff(moment.utc(t).local(), 'days'));
    return n === 0 ? 'today' : `${n} day${n === 1 ? '' : 's'} ago`;
};
const cellLabel = (c) => `${fmtDay(c.d)} ${c.sh}`;
const listCells = (cs, max = 4) => cs.slice(0, max).map(cellLabel).join(' · ') + (cs.length > max ? ` +${cs.length - max} more` : '');

/**
 * What's missing right now, most urgent first. Each item:
 * { key, level: 'critical'|'warning'|'info', title, detail }.
 */
export const attentionItems = (models, jobs, now = moment()) => {
    const items = [];
    const yesterday = ymd(now.clone().subtract(1, 'day'));
    const tag = (job) => (jobs.length > 1 ? ` · ${job}` : '');
    models.forEach((m) => {
        // Only days from the tracking start (when there is one) count as
        // missing — the days before it predate the ticket process.
        const inScope = (c) => !m.trackStart || c.d >= m.trackStart;

        m.cells.filter((c) => c.state === 'returned').forEach((c) => items.push({
            level: 'critical',
            title: `Returned by client: ${cellLabel(c)} ticket${tag(m.job)}`,
            detail: c.env?.Declined_Reason ? `“${String(c.env.Declined_Reason).slice(0, 90)}”` : 'Fix it and send it back out for signature',
        }));

        const waiting = m.cells.filter((c) => c.state === 'waiting')
            .sort((a, b) => String(a.env?.Sent_At_Utc || '').localeCompare(String(b.env?.Sent_At_Utc || '')));
        if (waiting.length) {
            const oldest = waiting[0];
            const sent = oldest.env?.Sent_At_Utc;
            const age = sent ? Math.max(0, now.diff(moment.utc(sent).local(), 'days')) : null;
            const opened = String(oldest.env?.Status || '').toLowerCase() === 'delivered';
            items.push({
                level: age != null && age >= WAIT_ALERT_DAYS ? 'warning' : 'info',
                title: `${waiting.length} ticket${waiting.length === 1 ? '' : 's'} waiting on client signature${tag(m.job)}`,
                detail: `Oldest: ${cellLabel(oldest)}`
                    + (sent ? ` — sent ${sentAgo(sent, now)}` : '')
                    + (opened ? ', opened but not signed' : '')
                    + (oldest.env?.Signer_Name ? ` (${oldest.env.Signer_Name})` : ''),
            });
        }

        const unsent = m.cells.filter((c) => c.worked && inScope(c) && (c.state === 'none' || c.state === 'prep'))
            .sort((a, b) => a.d.localeCompare(b.d) || a.sh.localeCompare(b.sh));
        const late = unsent.filter((c) => c.d < yesterday);
        const fresh = unsent.filter((c) => c.d >= yesterday);
        if (late.length) {
            items.push({
                level: 'warning',
                title: `${late.length} worked shift${late.length === 1 ? '' : 's'} with no ticket sent${tag(m.job)}`,
                detail: listCells(late),
            });
        }
        if (fresh.length) {
            items.push({
                level: 'info',
                title: `Yesterday's ticket${fresh.length === 1 ? '' : 's'} not sent yet${tag(m.job)}`,
                detail: listCells(fresh),
            });
        }

        m.billed.kickedBack.forEach((no) => items.push({
            level: 'warning', title: `Billing summary ${no} was kicked back${tag(m.job)}`, detail: 'Needs rework before it goes back up',
        }));

        const signedUnbilled = m.cells.filter((c) => c.state === 'signed' && !c.billed && c.d > (m.billed.through || ''));
        if (signedUnbilled.length) {
            items.push({
                level: 'info',
                title: `${signedUnbilled.length} signed shift${signedUnbilled.length === 1 ? '' : 's'} not billed yet${tag(m.job)}`,
                detail: listCells(signedUnbilled),
            });
        }

        const y = m.crew[yesterday];
        if (y && y.split && m.hasRoster && y.unassigned > 0) {
            items.push({
                level: 'info',
                title: `${y.unassigned} ${y.unassigned === 1 ? 'person' : 'people'} on yesterday's cards not on the Day/Night roster${tag(m.job)}`,
                detail: 'Their shift was taken from the HeavyJob card',
            });
        }
    });
    const rank = { critical: 0, warning: 1, info: 2 };
    return items
        .map((it, i) => ({ ...it, i }))
        .sort((a, b) => rank[a.level] - rank[b.level] || a.i - b.i)
        .map(({ i, ...it }) => ({ ...it, key: `${it.level}-${it.title}` }));
};

export const EVENT_TEXT = {
    sent: 'Sent to',
    delivered: 'Opened by',
    completed: 'Signed by',
    declined: 'Returned by',
};

/** Client-facing signature events across jobs, newest first. */
export const clientActivity = (models, max = 7) => {
    const out = [];
    models.forEach((m) => (m.envelopes || []).forEach((e) => {
        // Test sends (Doc_ID 'test…') only ever reached the sender.
        if (e.Shift === 'Force' || /^test/i.test(String(e.Doc_ID || ''))) return;
        const what = e.Shift === 'Doc'
            ? String(e.Document_Name || e.Subject || 'Ticket packet').replace(/\.(xlsx|pdf)$/i, '')
            : `${fmtDay(e.Work_Date)} ${e.Shift} ticket`;
        const seen = new Set();
        (e.events || []).forEach((v) => {
            const ev = String(v.Event || '').toLowerCase();
            if (!EVENT_TEXT[ev] || seen.has(ev)) return;
            seen.add(ev);
            out.push({ key: `${m.job}-${e.RowID}-${ev}`, job: m.job, ev, what, who: e.Signer_Name || 'client', at: v.Occurred_At_Utc });
        });
    }));
    return out.sort((a, b) => String(b.at).localeCompare(String(a.at))).slice(0, max);
};

// ── Look (light, one committed palette; Day/Night validated on white) ──

const C = {
    page: '#f4f4f1',
    panel: '#ffffff',
    line: 'rgba(11,11,11,0.09)',
    grid: '#ebeae4',
    base: '#c3c2b7',
    ink: '#0b0b0b',
    ink2: '#52514e',
    muted: '#898781',
    weekend: '#efeee8',
    good: '#0ca30c',
    goodText: '#006300',
    warning: '#fab219',
    warningText: '#8a5a00',
    critical: '#d03b3b',
};
const SHIFT_COLOR = { Day: '#eb6834', Night: '#4a3aa7' };
const TOTAL_COLOR = '#8e8c85';

const STATE = {
    signed: { label: 'Signed', glyph: '✓', fill: '#e3f4e1', stroke: C.good, ink: C.goodText },
    waiting: { label: 'Waiting on client', glyph: '◷', fill: '#fdf0cc', stroke: C.warning, ink: C.warningText },
    returned: { label: 'Returned', glyph: '✕', fill: '#f8dddd', stroke: C.critical, ink: C.critical },
    prep: { label: 'Being prepared', glyph: '…', fill: '#ffffff', stroke: '#b9b7ae', ink: C.ink2 },
    unsent: { label: 'Not sent', glyph: '', fill: '#ffffff', stroke: '#c9c7be', ink: C.muted },
};

const LEVEL = {
    critical: { bg: C.critical, glyph: '!' },
    warning: { bg: C.warning, glyph: '!' },
    info: { bg: '#a3a197', glyph: 'i' },
};

const CSS = `
.btv{position:fixed;inset:0;z-index:2000;background:${C.page};color:${C.ink};
  font-family:system-ui,-apple-system,"Segoe UI",sans-serif;display:flex;flex-direction:column;overflow:hidden;-webkit-font-smoothing:antialiased}
.btv.idle{cursor:none}
.btv *{box-sizing:border-box}
.btv-top{display:flex;align-items:center;gap:1.4vw;padding:1.5vh 1.6vw 0.6vh;flex-shrink:0}
.btv-top img{height:3.6vh;width:auto;display:block}
.btv-sep{width:1px;height:3.8vh;background:${C.base}}
.btv-title{display:flex;flex-direction:column;min-width:0;margin-left:0.6vw}
.btv-title b{font-size:clamp(16px,1.45vw,32px);font-weight:700;letter-spacing:-0.01em;white-space:nowrap}
.btv-title span{font-size:clamp(11px,0.8vw,17px);color:${C.ink2};white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.btv-clock{margin-left:auto;text-align:right}
.btv-clock b{display:block;font-size:clamp(20px,2.2vw,48px);font-weight:700;font-variant-numeric:tabular-nums;line-height:1}
.btv-clock span{font-size:clamp(11px,0.8vw,17px);color:${C.ink2}}
.btv-body{flex:1;display:grid;grid-template-columns:repeat(12,minmax(0,1fr));grid-template-rows:auto minmax(0,1fr);gap:1vw;padding:0.8vh 1.6vw 0.4vh;min-height:0}
.btv-kpis{grid-column:1/-1;display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:1vw}
.btv-card{background:${C.panel};border:1px solid ${C.line};border-radius:12px;padding:1.4vh 1.1vw;min-width:0;min-height:0;display:flex;flex-direction:column;overflow:hidden}
.btv-lbl{font-size:clamp(10px,0.72vw,16px);font-weight:700;letter-spacing:0.08em;color:${C.muted};text-transform:uppercase;white-space:nowrap}
.btv-val{display:flex;align-items:baseline;gap:0.6vw;margin-top:0.5vh;white-space:nowrap}
.btv-val b{font-size:clamp(26px,2.8vw,62px);font-weight:700;letter-spacing:-0.02em;line-height:1.05}
.btv-val em{font-style:normal;font-size:clamp(12px,1vw,22px);color:${C.ink2};font-weight:600}
.btv-sub{font-size:clamp(11px,0.82vw,18px);color:${C.ink2};margin-top:0.5vh;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.btv-sub i{display:inline-block;width:0.6vw;height:0.6vw;min-width:8px;min-height:8px;border-radius:2px;margin-right:0.3vw}
.btv-head{display:flex;align-items:baseline;gap:1.1vw;margin-bottom:0.8vh}
.btv-head h3{margin:0;font-size:clamp(13px,1.05vw,23px);font-weight:700;white-space:nowrap}
.btv-head .btv-lbl{margin-left:auto}
.btv-legend{display:flex;gap:0.9vw;font-size:clamp(10px,0.76vw,17px);color:${C.ink2};white-space:nowrap;align-items:center;min-width:0;overflow:hidden}
.btv-legend i{display:inline-block;width:0.65vw;height:0.65vw;min-width:9px;min-height:9px;border-radius:2px;margin-right:0.35vw;vertical-align:-1px}
.btv-chart{flex:1;min-height:0;position:relative}
.btv-chart svg{position:absolute;inset:0;width:100%;height:100%;overflow:visible}
.btv-item{display:flex;gap:0.8vw;padding:1vh 0;border-top:1px solid ${C.grid}}
.btv-item:first-child{border-top:none;padding-top:0.2vh}
.btv-item b{display:block;font-size:clamp(12px,0.95vw,21px);font-weight:700;line-height:1.25}
.btv-item span{display:block;font-size:clamp(11px,0.8vw,17px);color:${C.ink2};margin-top:0.3vh;line-height:1.3}
.btv-mark{flex-shrink:0;width:clamp(18px,1.5vw,32px);height:clamp(18px,1.5vw,32px);border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:clamp(11px,0.85vw,18px);color:#fff}
.btv-act{display:grid;grid-template-columns:auto minmax(0,1fr) auto;gap:0 0.8vw;align-items:baseline;padding:0.8vh 0;border-top:1px solid ${C.grid};font-size:clamp(11px,0.85vw,18px)}
.btv-act:first-child{border-top:none;padding-top:0.2vh}
.btv-empty{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;color:${C.muted};font-size:clamp(12px,0.95vw,20px);text-align:center;gap:0.8vh;padding:0 1vw}
.btv-foot{display:flex;align-items:center;gap:1vw;padding:0.6vh 1.6vw 1vh;font-size:clamp(10px,0.7vw,15px);color:${C.muted};flex-shrink:0}
.btv-dot{width:7px;height:7px;border-radius:50%;background:${C.good};animation:btvPulse 2.4s infinite}
.btv-dot.err{background:${C.warning};animation:none}
@keyframes btvPulse{0%{box-shadow:0 0 0 0 rgba(12,163,12,.45)}70%{box-shadow:0 0 0 7px rgba(12,163,12,0)}100%{box-shadow:0 0 0 0 rgba(12,163,12,0)}}
.btv-ctl{position:absolute;right:1.6vw;bottom:3.4vh;display:flex;gap:8px;transition:opacity .3s}
.btv.idle .btv-ctl{opacity:0;pointer-events:none}
.btv-ctl button{background:#fff;color:${C.ink};border:1px solid ${C.base};border-radius:6px;padding:7px 12px;font:600 13px system-ui;cursor:pointer}
.btv-skel{background:linear-gradient(90deg,#efeee9 0%,#f7f6f2 50%,#efeee9 100%);background-size:200% 100%;animation:btvSk 1.4s infinite;border-radius:6px}
@keyframes btvSk{0%{background-position:200% 0}100%{background-position:-200% 0}}
`;

// ── Pieces ──

// The panel's rendered size, so the timeline draws in real pixels (crisp
// bars, label sizes that scale with the monitor) rather than a stretched box.
const useBox = () => {
    const ref = useRef(null);
    const [box, setBox] = useState({ w: 1000, h: 500 });
    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        const measure = () => {
            const r = el.getBoundingClientRect();
            if (r.width && r.height) setBox({ w: r.width, h: r.height });
        };
        measure();
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        if (ro) ro.observe(el);
        window.addEventListener('resize', measure);
        return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', measure); };
    }, []);
    return [ref, box];
};

const niceMax = (v) => {
    if (v <= 0) return 10;
    const p = 10 ** Math.floor(Math.log10(v));
    const n = v / p;
    return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * p;
};

// Top corners rounded, flat on the baseline.
const barPath = (x, y, w, h, r) => {
    const rr = Math.max(0, Math.min(r, w / 2, h));
    return `M${x},${y + h}V${y + rr}Q${x},${y} ${x + rr},${y}H${x + w - rr}Q${x + w},${y} ${x + w},${y + rr}V${y + h}Z`;
};

/**
 * The timeline: stacked Day/Night crew bars over the window and, under each
 * day, the ticket cells for every job × shift lane — one shared x axis, with
 * weekends shaded straight through both.
 */
const Timeline = ({ days, crew, lanes }) => {
    const [ref, box] = useBox();
    const W = box.w;
    const H = box.h;
    const fs = Math.max(11, Math.min(20, H / 30));
    const padL = fs * 5;
    const plotW = Math.max(10, W - padL - 2);
    const slot = plotW / days.length;
    const laneH = Math.max(fs * 1.9, Math.min(fs * 2.6, H * 0.075));
    const laneGap = Math.max(3, fs * 0.28);
    const stripH = lanes.length * (laneH + laneGap);
    const axisH = fs * 2.9;
    const topPad = fs * 1.8;
    const chartH = Math.max(40, H - stripH - axisH - topPad - fs * 0.4);
    const baseY = topPad + chartH;
    const max = niceMax(Math.max(0, ...days.map((d) => crew[d]?.total || 0)) * 1.08);
    const y = (v) => baseY - (v / max) * chartH;
    const bw = Math.max(4, Math.min(slot * 0.66, fs * 3.2));
    const stripY = baseY + axisH;
    const cellW = Math.max(4, slot - Math.max(3, slot * 0.14));
    const last = days[days.length - 1];
    return (
        <div className="btv-chart" ref={ref}>
            <svg viewBox={`0 0 ${W} ${H}`}>
                {days.map((d, i) => (isWeekend(d) ? (
                    <rect key={`w${d}`} x={padL + slot * i} y={0} width={slot} height={H} fill={C.weekend} />
                ) : null))}
                <text x={0} y={topPad - fs * 0.7} fill={C.muted} fontSize={fs * 0.78} fontWeight={700} letterSpacing="0.08em">PEOPLE</text>
                {[0, 0.5, 1].map((f) => (
                    <g key={f}>
                        <line x1={padL} x2={W} y1={y(f * max)} y2={y(f * max)} stroke={f === 0 ? C.base : C.grid} strokeWidth={1} />
                        <text x={padL - fs * 0.6} y={y(f * max) + fs * 0.35} fill={C.muted} fontSize={fs * 0.85} textAnchor="end" style={{ fontVariantNumeric: 'tabular-nums' }}>{Math.round(f * max)}</text>
                    </g>
                ))}
                {days.map((d, i) => {
                    const c = crew[d];
                    if (!c || !c.total) return null;
                    const x = padL + slot * i + (slot - bw) / 2;
                    const segs = c.split
                        ? SHIFTS.filter((sh) => c[sh] > 0).map((sh) => ({ sh, v: c[sh] }))
                        : [{ sh: 'total', v: c.total }];
                    let acc = 0;
                    const isLast = d === last;
                    return (
                        <g key={`b${d}`}>
                            <title>{`${fmtDay(d)} — ${c.total} people${c.split ? ` (Day ${c.Day}, Night ${c.Night})` : ''}`}</title>
                            {segs.map((s, si) => {
                                const top = y(acc + s.v);
                                // 2px surface gap between stacked segments.
                                const h = (s.v / max) * chartH - (si > 0 ? 2 : 0);
                                acc += s.v;
                                const fill = s.sh === 'total' ? TOTAL_COLOR : SHIFT_COLOR[s.sh];
                                return si === segs.length - 1
                                    ? <path key={s.sh} d={barPath(x, top, bw, Math.max(0, h), 4)} fill={fill} />
                                    : <rect key={s.sh} x={x} y={top} width={bw} height={Math.max(0, h)} fill={fill} />;
                            })}
                            {isLast || slot > fs * 1.9 ? (
                                <text x={x + bw / 2} y={y(c.total) - fs * 0.4} fill={isLast ? C.ink : C.ink2} fontSize={isLast ? fs * 1.05 : fs * 0.78} fontWeight={isLast ? 700 : 600} textAnchor="middle">{c.total}</text>
                            ) : null}
                        </g>
                    );
                })}
                {days.map((d, i) => {
                    const cx = padL + slot * i + slot / 2;
                    const m = moment.utc(d, 'YYYY-MM-DD');
                    const bold = d === last;
                    const wk = isWeekend(d);
                    return (
                        <g key={`x${d}`}>
                            <text x={cx} y={baseY + fs * 1.15} fill={wk ? C.ink2 : C.muted} fontSize={fs * 0.76} fontWeight={wk || bold ? 700 : 500} textAnchor="middle">{m.format('dd').toUpperCase()}</text>
                            {slot > fs * 2.2 || i % 2 === (days.length - 1) % 2 ? (
                                <text x={cx} y={baseY + fs * 2.25} fill={bold ? C.ink : C.muted} fontSize={fs * 0.85} fontWeight={bold ? 700 : 400} textAnchor="middle">{m.format('M/D')}</text>
                            ) : null}
                        </g>
                    );
                })}
                {lanes.map((lane, li) => {
                    const ly = stripY + li * (laneH + laneGap);
                    return (
                        <g key={lane.key}>
                            <rect x={0} y={ly + laneH * 0.25} width={fs * 0.35} height={laneH * 0.5} rx={2} fill={SHIFT_COLOR[lane.sh]} />
                            <text x={fs * 0.75} y={ly + laneH / 2 + fs * 0.34} fill={C.ink} fontSize={fs * 0.88} fontWeight={700}>{lane.label}</text>
                            {days.map((d, i) => {
                                const c = lane.cells[d];
                                const x = padL + slot * i + (slot - cellW) / 2;
                                if (!c || (!c.worked && c.state === 'none')) {
                                    return <line key={d} x1={x + cellW * 0.32} x2={x + cellW * 0.68} y1={ly + laneH / 2} y2={ly + laneH / 2} stroke={C.base} strokeWidth={1.5} strokeLinecap="round" />;
                                }
                                const k = c.state === 'none' ? 'unsent' : c.state;
                                const s = STATE[k];
                                const opened = c.state === 'waiting' && String(c.env?.Status || '').toLowerCase() === 'delivered';
                                return (
                                    <g key={d}>
                                        <title>{`${lane.label} · ${fmtDay(d)} — ${s.label}${opened ? ' (opened)' : ''}${c.billed ? ' · billed' : ''}`}</title>
                                        <rect x={x} y={ly} width={cellW} height={laneH} rx={Math.min(6, cellW / 4)} fill={s.fill} stroke={s.stroke} strokeWidth={k === 'unsent' ? 1.5 : 2} strokeDasharray={k === 'unsent' ? '4 3' : undefined} />
                                        {s.glyph && cellW > fs * 1.1 ? (
                                            <text x={x + cellW / 2} y={ly + laneH / 2 + fs * 0.38} fill={s.ink} fontSize={fs * 1.05} fontWeight={800} textAnchor="middle">{s.glyph}</text>
                                        ) : null}
                                        {c.billed ? <rect x={x + cellW * 0.2} y={ly + laneH - 5} width={cellW * 0.6} height={3} rx={1.5} fill={C.ink} /> : null}
                                    </g>
                                );
                            })}
                        </g>
                    );
                })}
            </svg>
        </div>
    );
};

const Kpi = ({ label, loading, children }) => (
    <div className="btv-card">
        <div className="btv-lbl">{label}</div>
        {loading ? <div className="btv-skel" style={{ height: '5vh', marginTop: '0.8vh' }} /> : children}
    </div>
);

// ── Board ──

const BillingDashboard = ({ jobs = DASHBOARD_JOBS, onExit }) => {
    const [feeds, setFeeds] = useState({});
    const [loaded, setLoaded] = useState(false);
    const [updatedAt, setUpdatedAt] = useState(null);
    const [failed, setFailed] = useState(false);
    const [now, setNow] = useState(moment());
    const [idle, setIdle] = useState(false);
    const rootRef = useRef(null);
    const startedAt = useRef(Date.now());
    // Jobs readCrewShifts has answered for. A later failed pass keeps their
    // last crew (the footer dot says so) rather than dropping the chart to
    // the cards' totals, which carry no day/night split.
    const hadCrew = useRef({});

    // Feeds: every job's sources in parallel, every REFRESH_MS. A source that
    // fails keeps its last good value and the footer dot says so.
    const jobsKey = jobs.join(',');
    useEffect(() => {
        let dead = false;
        const ok = (res) => res && !res.error;
        const pass = async () => {
            let bad = false;
            const next = {};
            await Promise.all(jobs.map(async (j) => {
                const f = {};
                const [ud, sg, ro, cs] = await Promise.all([
                    // Billing rows are job-level (BILLING_VERSION), not on the
                    // weekly version — see the store comment.
                    loadUserData({ id: j, forecastGuid: BILLING_VERSION, live: true }).catch(() => null),
                    loadBillingSignatures({ job: j }).catch(() => null),
                    loadAttendanceRoster({ job: j }).catch(() => null),
                    loadCrewShifts({ id: j, days: WINDOW_DAYS + 7 }).catch(() => null),
                ]);
                if (ok(ud)) f.rows = (ud.data || []).filter((x) => x?.Forecast_Tab === 'Billing'); else bad = true;
                if (ok(sg)) f.sigs = sg.envelopes || []; else bad = true;
                if (ok(ro)) f.roster = ro; // no roster filed yet is normal
                if (ok(cs) && Array.isArray(cs.data)) {
                    f.people = cs.data;
                    hadCrew.current[j] = true;
                } else if (hadCrew.current[j]) {
                    bad = true;
                } else {
                    // The same window off the live cards, one call (readHeavyJobCards
                    // takes up to 31 days; this is 29).
                    const h = await Promise.resolve().then(() => loadHeavyJobCards({
                        job: j,
                        start: ymd(moment().subtract(WINDOW_DAYS + 7, 'days')),
                        end: ymd(moment()),
                    })).catch(() => null);
                    if (ok(h) && h.configured !== false) f.dates = (h.dates || []).map((x) => ({ Work_Date: x.Work_Date, Staff: x.people }));
                    else bad = true;
                }
                next[j] = f;
            }));
            if (dead) return;
            setFeeds((cur) => {
                const merged = { ...cur };
                Object.entries(next).forEach(([j, f]) => { merged[j] = { ...(cur[j] || {}), ...f }; });
                return merged;
            });
            setFailed(bad);
            setLoaded(true);
            setUpdatedAt(moment());
        };
        pass();
        const id = setInterval(pass, REFRESH_MS);
        return () => { dead = true; clearInterval(id); };
    }, [jobsKey]);

    // Clock, plus a once-a-night reload (4 AM) so the screen picks up app
    // deploys and never runs for weeks on one page load.
    useEffect(() => {
        const id = setInterval(() => {
            const m = moment();
            setNow(m);
            if (m.hour() === 4 && Date.now() - startedAt.current > 6 * 3600 * 1000) window.location.reload();
        }, 15000);
        return () => clearInterval(id);
    }, []);

    // Keep the monitor awake while the board is up (where the browser allows).
    useEffect(() => {
        let lock = null;
        const grab = async () => {
            try {
                if (navigator.wakeLock && document.visibilityState === 'visible') lock = await navigator.wakeLock.request('screen');
            } catch (e) { /* unsupported or denied — the power plan decides */ }
        };
        grab();
        const onVis = () => { if (document.visibilityState === 'visible') grab(); };
        document.addEventListener('visibilitychange', onVis);
        return () => {
            document.removeEventListener('visibilitychange', onVis);
            if (lock) lock.release().catch(() => {});
        };
    }, []);

    // Cursor and controls fade after a few seconds without the mouse.
    useEffect(() => {
        let t = null;
        const wake = () => {
            setIdle(false);
            clearTimeout(t);
            t = setTimeout(() => setIdle(true), 3500);
        };
        wake();
        window.addEventListener('mousemove', wake);
        window.addEventListener('keydown', wake);
        return () => { clearTimeout(t); window.removeEventListener('mousemove', wake); window.removeEventListener('keydown', wake); };
    }, []);

    const todayKey = ymd(now);
    const days = useMemo(() => windowDays(todayKey), [todayKey]);
    // One Attendance & Force Report master covers the sister jobs, filed on
    // one job's site only (54026070's) — a job without its own roster uses
    // the one that was found; rosterShiftOf keeps only its own rows.
    const models = useMemo(
        () => {
            const shared = jobs.map((j) => feeds[j]?.roster).find((r) => r?.found) || null;
            return jobs.map((j) => {
                const f = feeds[j] || {};
                return jobModel(j, f.roster?.found ? f : { ...f, roster: shared }, days);
            });
        },
        [feeds, days, jobsKey],
    );

    // Crew across jobs per day.
    const crew = useMemo(() => {
        const out = {};
        days.forEach((d) => {
            const e = { Day: 0, Night: 0, total: 0, split: true };
            let any = false;
            models.forEach((m) => {
                const c = m.crew[d];
                if (!c) return;
                any = true;
                e.Day += c.Day; e.Night += c.Night; e.total += c.total;
                if (!c.split) e.split = false;
            });
            if (any) out[d] = e;
        });
        return out;
    }, [models, days]);
    const split = Object.values(crew).some((c) => c.split);

    // One ticket lane per job × shift with anything in the window.
    const lanes = useMemo(() => {
        const out = [];
        models.forEach((m) => SHIFTS.forEach((sh) => {
            const cs = m.cells.filter((c) => c.sh === sh);
            if (!cs.some((c) => c.worked || c.state !== 'none')) return;
            const byDay = {};
            cs.forEach((c) => { byDay[c.d] = c; });
            out.push({ key: `${m.job}-${sh}`, sh, label: `${jobs.length > 1 ? `${String(m.job).slice(-2)} ` : ''}${sh}`, cells: byDay });
        }));
        return out;
    }, [models, jobsKey]);

    // Same clock as the KPI strip, so "sent N days ago" agrees in both places.
    const items = useMemo(() => attentionItems(models, jobs, now), [models, jobsKey, now]);
    const activity = useMemo(() => clientActivity(models), [models]);

    // Headline numbers.
    const yesterday = days[days.length - 1];
    const worked = days.filter((d) => crew[d]?.total);
    const lastDay = worked[worked.length - 1] || null;
    const lastCrew = lastDay ? crew[lastDay] : null;
    // Shifts that owe a ticket: worked since the tracking start, plus any
    // earlier shift that got signed anyway (so a ✓ on the strip always counts).
    const owed = models.flatMap((m) => m.cells.filter((c) => (c.worked && (!m.trackStart || c.d >= m.trackStart)) || c.state === 'signed'));
    const signedN = owed.filter((c) => c.state === 'signed').length;
    const waitingCells = models.flatMap((m) => m.cells).filter((c) => c.state === 'waiting');
    const oldestSent = waitingCells.map((c) => c.env?.Sent_At_Utc).filter(Boolean).sort()[0] || null;
    const lastSigned = activity.find((a) => a.ev === 'completed') || null;
    const billedThrough = models.map((m) => m.billed.through).filter(Boolean).sort().pop() || '';

    const toggleFs = () => {
        const el = rootRef.current;
        if (!document.fullscreenElement && el?.requestFullscreen) el.requestFullscreen().catch(() => {});
        else if (document.exitFullscreen) document.exitFullscreen().catch(() => {});
    };

    return (
        <div className={`btv${idle ? ' idle' : ''}`} ref={rootRef}>
            <style>{CSS}</style>

            <div className="btv-top">
                <img src={strikeLogo} alt="Strike" />
                <div className="btv-sep" />
                <img src={voltaLogo} alt="VoltaGrid" />
                <div className="btv-title">
                    <b>Crew &amp; Field Tickets</b>
                    <span>{jobs.join('  ·  ')}</span>
                </div>
                <div className="btv-clock">
                    <b>{now.format('h:mm A')}</b>
                    <span>{now.format('dddd, MMMM D')}</span>
                </div>
            </div>

            <div className="btv-body">
                <div className="btv-kpis">
                    <Kpi label={lastDay ? `Crew · ${fmtDay(lastDay)}` : 'Crew'} loading={!loaded}>
                        <div className="btv-val"><b>{lastCrew ? lastCrew.total : '—'}</b><em>people</em></div>
                        <div className="btv-sub">
                            {lastCrew && lastCrew.split ? (
                                <>
                                    <i style={{ background: SHIFT_COLOR.Day }} />Day {lastCrew.Day}
                                    <span style={{ margin: '0 0.7vw', color: C.base }}>|</span>
                                    <i style={{ background: SHIFT_COLOR.Night }} />Night {lastCrew.Night}
                                </>
                            ) : lastCrew ? 'On HeavyJob time cards' : 'No time cards yet'}
                        </div>
                    </Kpi>
                    <Kpi label="Waiting on client signature" loading={!loaded}>
                        <div className="btv-val">
                            <b style={{ color: waitingCells.length ? C.warningText : C.ink }}>{waitingCells.length}</b>
                            <em>{waitingCells.length === 1 ? 'shift' : 'shifts'}</em>
                        </div>
                        <div className="btv-sub">{oldestSent ? `Oldest sent ${sentAgo(oldestSent, now)}` : 'Nothing out for signature'}</div>
                    </Kpi>
                    <Kpi label="Tickets signed" loading={!loaded}>
                        <div className="btv-val"><b>{signedN}</b><em>of {owed.length} shifts worked</em></div>
                        <div className="btv-sub">{lastSigned ? `Last signed ${moment.utc(lastSigned.at).local().fromNow()} · ${lastSigned.who}` : 'No signatures yet'}</div>
                    </Kpi>
                    <Kpi label="Billed through" loading={!loaded}>
                        <div className="btv-val">
                            <b>{billedThrough ? moment.utc(billedThrough, 'YYYY-MM-DD').format('MMM D') : '—'}</b>
                            {billedThrough ? <em>{Math.max(0, dayDiff(billedThrough, yesterday))} days back</em> : null}
                        </div>
                        <div className="btv-sub">{billedThrough ? 'Latest work date on a submitted summary' : 'No summaries submitted yet'}</div>
                    </Kpi>
                </div>

                <div className="btv-card" style={{ gridColumn: 'span 8' }}>
                    <div className="btv-head">
                        <h3>Crew and ticket status</h3>
                        <div className="btv-legend">
                            {split
                                ? SHIFTS.map((sh) => <span key={sh}><i style={{ background: SHIFT_COLOR[sh] }} />{sh}</span>)
                                : <span><i style={{ background: TOTAL_COLOR }} />People</span>}
                            <span style={{ width: 1, height: '1.6vh', background: C.base, margin: '0 0.2vw' }} />
                            {['signed', 'waiting', 'returned', 'unsent'].map((k) => (
                                <span key={k}>
                                    <i style={{ background: STATE[k].fill, border: `1.5px ${k === 'unsent' ? 'dashed' : 'solid'} ${STATE[k].stroke}`, borderRadius: 3 }} />
                                    {STATE[k].label}
                                </span>
                            ))}
                            <span><i style={{ background: C.ink, height: 3, minHeight: 3, borderRadius: 1, verticalAlign: 'middle' }} />Billed</span>
                        </div>
                        <span className="btv-lbl">Last 3 weeks</span>
                    </div>
                    {!loaded ? <div className="btv-skel" style={{ flex: 1 }} /> : <Timeline days={days} crew={crew} lanes={lanes} />}
                </div>

                <div style={{ gridColumn: 'span 4', display: 'flex', flexDirection: 'column', gap: '1vw', minHeight: 0 }}>
                    <div className="btv-card" style={{ flex: 1.4 }}>
                        <div className="btv-head">
                            <h3>Needs attention</h3>
                            {items.length ? <span className="btv-lbl">{items.length} open</span> : null}
                        </div>
                        {!loaded ? <div className="btv-skel" style={{ flex: 1 }} />
                            : items.length ? (
                                <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                                    {items.slice(0, 6).map((it) => (
                                        <div className="btv-item" key={it.key}>
                                            <div className="btv-mark" style={{ background: LEVEL[it.level].bg }}>{LEVEL[it.level].glyph}</div>
                                            <div style={{ minWidth: 0 }}>
                                                <b>{it.title}</b>
                                                {it.detail ? <span>{it.detail}</span> : null}
                                            </div>
                                        </div>
                                    ))}
                                    {items.length > 6 ? <div className="btv-sub">+{items.length - 6} more</div> : null}
                                </div>
                            ) : (
                                <div className="btv-empty">
                                    <div className="btv-mark" style={{ background: C.good, width: 'clamp(34px,3vw,64px)', height: 'clamp(34px,3vw,64px)', fontSize: 'clamp(18px,1.6vw,34px)' }}>✓</div>
                                    <b style={{ color: C.ink, fontSize: 'clamp(14px,1.2vw,26px)' }}>All caught up</b>
                                    <span>Every worked shift has its ticket signed or out for signature.</span>
                                </div>
                            )}
                    </div>
                    <div className="btv-card" style={{ flex: 1 }}>
                        <div className="btv-head">
                            <h3>Latest from the client</h3>
                        </div>
                        {!loaded ? <div className="btv-skel" style={{ flex: 1 }} />
                            : activity.length ? (
                                <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                                    {activity.slice(0, 5).map((a) => {
                                        const tone = a.ev === 'completed' ? C.goodText : a.ev === 'declined' ? C.critical : a.ev === 'delivered' ? C.warningText : C.ink2;
                                        const glyph = a.ev === 'completed' ? '✓' : a.ev === 'declined' ? '✕' : a.ev === 'delivered' ? '◉' : '↗';
                                        return (
                                            <div className="btv-act" key={a.key}>
                                                <span style={{ color: tone, fontWeight: 800 }}>{glyph}</span>
                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    <b>{EVENT_TEXT[a.ev]} {a.who}</b>
                                                    <span style={{ color: C.ink2 }}>{` · ${a.what}${jobs.length > 1 ? ` · ${String(a.job).slice(-2)}` : ''}`}</span>
                                                </span>
                                                <span style={{ color: C.muted, whiteSpace: 'nowrap' }}>{moment.utc(a.at).local().fromNow()}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : <div className="btv-empty">Signatures show up here as the client opens and signs tickets.</div>}
                    </div>
                </div>
            </div>

            <div className="btv-foot">
                <span className={`btv-dot${failed ? ' err' : ''}`} />
                <span>
                    {updatedAt ? `Updated ${updatedAt.format('h:mm A')}` : 'Loading…'}
                    {failed ? ' · some data didn’t refresh, retrying' : ' · refreshes every 5 minutes'}
                </span>
                <span style={{ marginLeft: 'auto' }}>
                    {split ? 'Crew from HeavyJob time cards, shifts per the attendance roster' : 'Crew from HeavyJob time cards'} · signatures from DocuSign
                </span>
            </div>

            <div className="btv-ctl">
                <button type="button" onClick={toggleFs}>Full screen</button>
                {onExit ? <button type="button" onClick={onExit}>Exit</button> : null}
            </div>
        </div>
    );
};

export default BillingDashboard;
